import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, UserPlus } from "lucide-react";
import { usersApi } from "@/api/users";
import type { AssignRolesPayload, CreateUserPayload, UpdateUserPayload } from "@/api/users";
import { ApiError } from "@/api/client";
import { Badge, Button, FormField, Input, PasswordInput } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { RoleChip } from "@/components/shared/RoleChip";
import {
  IMPERSONATE_PERMISSION,
  LogInAsAction,
} from "@/components/impersonation/LogInAsAction";
import { EnsureQaUserModal } from "@/components/users/EnsureQaUserModal";
import { RoleGrantCommissionNotice } from "@/components/users/RoleGrantCommissionNotice";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRoles";
import type { UserSummaryDto } from "@/types/api";
import { useDateFormatter } from "@/utils/datetime";

type ModalKind =
  | null
  | { type: "invite" }
  | { type: "edit"; user: UserSummaryDto }
  | { type: "roles"; user: UserSummaryDto }
  | { type: "deactivate"; user: UserSummaryDto }
  | { type: "set-password"; user: UserSummaryDto }
  | { type: "qa-user"; roleCode?: string };

export function UsersPage() {
  const { hasPermission, hasRoleCode } = useAuth();
  const canCreate = hasPermission("Users.Create");
  const canUpdate = hasPermission("Users.Update");
  const canDelete = hasPermission("Users.Delete");
  // Admin User Impersonation — the permission is the capability, ADMIN is the
  // role allowed to exercise it. Both, matching LogInAsAction's own gate.
  const canImpersonate =
    hasPermission(IMPERSONATE_PERMISSION) && hasRoleCode("ADMIN");

  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalKind>(null);

  // Status/role filtering and pagination are client-side, so fetch the
  // whole staff roster in one page (the firm's staff list is small).
  const users = useQuery({
    queryKey: ["users", search],
    queryFn: () => usersApi.list({ page: 1, pageSize: 500, q: search || undefined }),
  });
  // Admin-managed Lookups v1.0 §4d — role-assignment dropdowns now read
  // from the canonical active-roles list. New roles added via
  // /admin/roles immediately become assignable here without a redeploy.
  const rolesQuery = useRoles();
  const roleOptions = (rolesQuery.data ?? []).map((r) => ({
    code: r.code,
    title: r.displayName,
  }));

  // The ["users"] prefix also covers the shared assignable-users lookup
  // (["users", "assignable"], hooks/useAssignableUsers) and the @mention
  // search (["users", "search", …]). Name/role edits additionally feed the
  // batched staff-label resolver, which caches under its own root key.
  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });
  const invalidateUserLabels = () =>
    qc.invalidateQueries({ queryKey: ["internal-user-labels"] });

  const toggleRoleFilter = (code: string) =>
    setRoleFilter((cur) =>
      cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code],
    );

  // Inactive users are hidden unless opted in; no selected role chips = all.
  const rows = useMemo(() => {
    const all = users.data?.data ?? [];
    return all.filter(
      (u) =>
        (showInactive || u.isActive) &&
        (roleFilter.length === 0 || u.roleCodes.some((r) => roleFilter.includes(r))),
    );
  }, [users.data, showInactive, roleFilter]);

  // Drawer user is derived from the live query data so edits made through
  // the drawer's modals reflect immediately after invalidation.
  const detailUser = detailUserId
    ? (users.data?.data ?? []).find((u) => u.id === detailUserId) ?? null
    : null;

  const columns: Column<UserSummaryDto>[] = [
    {
      key: "displayName",
      header: "Name",
      render: (u) => (
        <div>
          <div className="font-semibold text-onyx">{u.displayName}</div>
          <div className="text-caption text-gray-mid">{u.email}</div>
        </div>
      ),
    },
    { key: "jobTitle", header: "Title", render: (u) => u.jobTitle ?? "—" },
    { key: "department", header: "Department", render: (u) => u.department ?? "—" },
    {
      key: "roles",
      header: "Role codes",
      render: (u) => (
        <div className="flex flex-wrap gap-space-1">
          {u.roleCodes.length ? (
            u.roleCodes.map((r) => <RoleChip key={r} code={r} />)
          ) : (
            <span className="text-caption text-gray-mid">No roles</span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (u) => (u.isActive ? <Badge status="finalized">Active</Badge> : <Badge status="archived">Inactive</Badge>),
    },
    {
      key: "actions",
      header: "",
      render: (u) => (
        <div className="flex justify-end gap-space-2">
          {/* Self-gating: renders nothing unless the caller may impersonate,
              and nothing at all for an ADMIN target (the backend refuses). */}
          <LogInAsAction
            target={{
              kind: "staff",
              userId: u.id,
              label: u.displayName,
              roleCodes: u.roleCodes,
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Details for ${u.displayName}`}
            onClick={() => setDetailUserId(u.id)}
          >
            Details
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="User management"
        actions={
          <div className="flex flex-wrap items-center gap-space-2">
            {canImpersonate && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setModal({ type: "qa-user" })}
              >
                <FlaskConical size={14} strokeWidth={1.5} aria-hidden /> QA test
                account
              </Button>
            )}
            {canCreate && (
              <Button size="sm" onClick={() => setModal({ type: "invite" })}>
                <UserPlus size={14} strokeWidth={1.5} aria-hidden /> Add User
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search name, email, title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <label className="flex items-center gap-space-2 text-small">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="accent-gold h-4 w-4"
          />
          Show inactive users
        </label>
      </div>

      {roleOptions.length > 0 && (
        <div className="flex flex-col gap-space-2 mb-space-3">
          <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
            Roles
          </div>
          <div className="flex flex-wrap gap-space-2">
            {roleOptions.map((r) => (
              <RoleFilterChip
                key={r.code}
                label={r.code}
                title={r.title}
                active={roleFilter.includes(r.code)}
                onClick={() => toggleRoleFilter(r.code)}
              />
            ))}
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={users.isLoading}
        error={users.isError ? (users.error as Error).message : null}
        emptyMessage="No users match your search."
        rowKey={(u) => u.id}
        pagination="auto"
      />

      {/* The exact situation QA provisioning exists for: most role codes have
          no real user behind them, so filtering by B3 or E1 returns an empty
          table — nobody to impersonate, and no way to see that role's app.
          The offer belongs HERE, where the admin just hit the wall. */}
      {canImpersonate &&
        !users.isLoading &&
        rows.length === 0 &&
        roleFilter.length > 0 && (
          <div className="mt-space-3 rounded-md border border-border-light bg-white-pure p-space-4 text-center">
            <p className="m-0 text-onyx">
              <span className="text-small">
                No user holds {roleFilter.join(" / ")}.
              </span>
            </p>
            <p className="mt-space-2 mb-space-3 text-gray-mid">
              <span className="text-caption">
                Create a QA test account for this role so there is somebody to
                sign in as.
              </span>
            </p>
            <Button
              size="sm"
              onClick={() =>
                setModal({ type: "qa-user", roleCode: roleFilter[0] })
              }
            >
              <FlaskConical size={14} strokeWidth={1.5} aria-hidden /> QA test
              account for {roleFilter[0]}
            </Button>
          </div>
        )}

      {detailUser && (
        <UserDetailsDrawer
          user={detailUser}
          onClose={() => setDetailUserId(null)}
          canUpdate={canUpdate}
          canDeactivate={canDelete}
          canImpersonate={canImpersonate}
          onEdit={() => setModal({ type: "edit", user: detailUser })}
          onChangeRoles={() => setModal({ type: "roles", user: detailUser })}
          onResetPassword={() => setModal({ type: "set-password", user: detailUser })}
          onDeactivate={() => setModal({ type: "deactivate", user: detailUser })}
        />
      )}

      {modal?.type === "invite" && (
        <InviteUserModal
          roles={roleOptions}
          onClose={() => setModal(null)}
          onInvited={() => {
            setModal(null);
            void invalidate();
          }}
        />
      )}
      {modal?.type === "edit" && (
        <EditUserModal
          user={modal.user}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            void invalidate();
            void invalidateUserLabels();
          }}
        />
      )}
      {modal?.type === "roles" && (
        <AssignRolesModal
          user={modal.user}
          roles={roleOptions}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            void invalidate();
            void invalidateUserLabels();
          }}
        />
      )}
      {modal?.type === "deactivate" && (
        <DeactivateModal
          user={modal.user}
          onClose={() => setModal(null)}
          onConfirmed={() => {
            setModal(null);
            // The row leaves the (default) active-only table view, so close
            // the drawer rather than leave it floating over nothing.
            setDetailUserId(null);
            void invalidate();
          }}
        />
      )}
      {modal?.type === "set-password" && (
        <SetPasswordModal
          user={modal.user}
          onClose={() => setModal(null)}
          onSaved={() => setModal(null)}
        />
      )}
      {modal?.type === "qa-user" && (
        <EnsureQaUserModal
          initialRoleCode={modal.roleCode}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

// Filter-chip toggle, mirroring the status filter on /admin/commissions.
function RoleFilterChip({
  label,
  title,
  active,
  onClick,
}: {
  label: string;
  title?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={
        "h-8 px-space-3 font-display text-label font-semibold uppercase tracking-label rounded-md " +
        "border transition-colors duration-fast " +
        (active
          ? "bg-gold text-onyx border-gold"
          : "bg-white-pure text-gray-mid border-border-light hover:border-gold")
      }
    >
      {label}
    </button>
  );
}

function UserDetailsDrawer({
  user,
  onClose,
  canUpdate,
  canDeactivate,
  canImpersonate,
  onEdit,
  onChangeRoles,
  onResetPassword,
  onDeactivate,
}: {
  user: UserSummaryDto;
  onClose: () => void;
  canUpdate: boolean;
  canDeactivate: boolean;
  canImpersonate: boolean;
  onEdit: () => void;
  onChangeRoles: () => void;
  onResetPassword: () => void;
  onDeactivate: () => void;
}) {
  const { date, dateTime } = useDateFormatter();
  const { toast } = useToast();
  // The footer only renders when there is something in it, so impersonation
  // has to count towards that — otherwise the one action an admin came for is
  // hidden on a row they cannot otherwise edit.
  const hasActions =
    canUpdate || (canDeactivate && user.isActive) || canImpersonate;

  const welcomeMutation = useMutation({
    mutationFn: () => usersApi.sendWelcome(user.id),
    onSuccess: () =>
      toast({ tone: "success", message: `Welcome email sent to ${user.email}.` }),
    onError: (err: Error) => toast({ tone: "error", message: err.message }),
  });

  return (
    <ResourceDrawer
      open
      onClose={onClose}
      title={user.displayName}
      subtitle={user.email}
      testId="user-details-drawer"
      footer={
        hasActions ? (
          <div className="flex flex-wrap items-center justify-end gap-space-2">
            <LogInAsAction
              target={{
                kind: "staff",
                userId: user.id,
                label: user.displayName,
                roleCodes: user.roleCodes,
              }}
            />
            {canUpdate && user.isActive && (
              <Button
                variant="secondary"
                size="sm"
                loading={welcomeMutation.isPending}
                onClick={() => welcomeMutation.mutate()}
              >
                Send welcome email
              </Button>
            )}
            {canUpdate && (
              <Button variant="secondary" size="sm" onClick={onEdit}>
                Edit details
              </Button>
            )}
            {canUpdate && (
              <Button variant="secondary" size="sm" onClick={onChangeRoles}>
                Change roles
              </Button>
            )}
            {canUpdate && (
              <Button variant="secondary" size="sm" onClick={onResetPassword}>
                Reset password
              </Button>
            )}
            {canDeactivate && user.isActive && (
              <Button variant="destructive" size="sm" onClick={onDeactivate}>
                Deactivate
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      <dl className="grid grid-cols-1 gap-space-3 m-0">
        <DrawerField label="Status">
          {user.isActive ? (
            <Badge status="finalized">Active</Badge>
          ) : (
            <Badge status="archived">Inactive</Badge>
          )}
        </DrawerField>
        <DrawerField label="Job title">{user.jobTitle ?? "—"}</DrawerField>
        <DrawerField label="Department">{user.department ?? "—"}</DrawerField>
        <DrawerField label="Role codes">
          {user.roleCodes.length ? (
            <div className="flex flex-wrap gap-space-1">
              {user.roleCodes.map((r) => (
                <RoleChip key={r} code={r} />
              ))}
            </div>
          ) : (
            <span className="text-caption text-gray-mid">No roles</span>
          )}
        </DrawerField>
        <DrawerField label="Primary role">
          {user.primaryRoleCode ?? "—"}
        </DrawerField>
        <DrawerField label="Created">{date(user.createdAt) || "—"}</DrawerField>
        <DrawerField label="Last login">
          {user.lastLoginAt ? dateTime(user.lastLoginAt) : "Never"}
        </DrawerField>
      </dl>
    </ResourceDrawer>
  );
}

function DrawerField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-space-1">
        {label}
      </dt>
      <dd className="m-0 text-small text-onyx">{children}</dd>
    </div>
  );
}

function SetPasswordModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserSummaryDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: (newPassword: string) => usersApi.setPassword(user.id, newPassword),
    onSuccess: () => {
      toast({ tone: "success", message: `Password set for ${user.email}.` });
      onSaved();
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.errors.length) {
        setErrors(err.errors);
      } else {
        setErrors([err.message]);
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Set password for ${user.email}`}
      description="The user can sign in immediately with this password, and must then set their own before using the app. They are not notified — share it through a secure channel."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErrors([]);
              mutation.mutate(password);
            }}
            loading={mutation.isPending}
            disabled={!password}
          >
            Save
          </Button>
        </>
      }
    >
      <FormField label="New password" required htmlFor="set-password-input">
        <PasswordInput
          id="set-password-input"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </FormField>

      {errors.length > 0 && (
        <ul className="mt-space-3 list-disc pl-space-4 text-caption text-status-error" role="alert">
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

interface RoleCodeOption {
  code: string;
  title: string;
}

function InviteUserModal({
  roles,
  onClose,
  onInvited,
}: {
  roles: RoleCodeOption[];
  onClose: () => void;
  onInvited: () => void;
}) {
  const [form, setForm] = useState<CreateUserPayload>({
    email: "",
    displayName: "",
    jobTitle: "",
    department: "",
    roleCodes: [],
    primaryRoleCode: undefined,
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateUserPayload) => usersApi.invite(body),
    onSuccess: onInvited,
    onError: (err: Error) => setError(err.message),
  });

  const toggleRole = (code: string) => {
    setForm((f) => {
      const has = f.roleCodes.includes(code);
      const next = has ? f.roleCodes.filter((r) => r !== code) : [...f.roleCodes, code];
      return {
        ...f,
        roleCodes: next,
        primaryRoleCode: next.includes(f.primaryRoleCode ?? "") ? f.primaryRoleCode : next[0],
      };
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add a New User"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              mutation.mutate({
                ...form,
                jobTitle: form.jobTitle || undefined,
                department: form.department || undefined,
                initialPassword: form.initialPassword || undefined,
              });
            }}
            loading={mutation.isPending}
            disabled={!form.email || !form.displayName || form.roleCodes.length === 0}
          >
            Add user
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        <FormField label="Email" required htmlFor="invite-email">
          <Input
            id="invite-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </FormField>
        <FormField label="Display name" required htmlFor="invite-name">
          <Input
            id="invite-name"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            required
          />
        </FormField>
        <FormField label="Job title" htmlFor="invite-title">
          <Input
            id="invite-title"
            value={form.jobTitle ?? ""}
            onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
          />
        </FormField>
        <FormField label="Department" htmlFor="invite-dept">
          <Input
            id="invite-dept"
            value={form.department ?? ""}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
          />
        </FormField>
      </div>

      <div className="mt-space-4">
        <FormField
          label="Initial password"
          htmlFor="invite-password"
          helperText="Leave blank to generate a random password no one knows — then send the user a welcome email so they can set their own. If you set one, the user must change it after their first sign-in."
        >
          <PasswordInput
            id="invite-password"
            autoComplete="new-password"
            value={form.initialPassword ?? ""}
            onChange={(e) => setForm({ ...form, initialPassword: e.target.value })}
          />
        </FormField>
      </div>

      <div className="mt-space-4">
        <div className="font-display text-caption font-semibold uppercase tracking-wide text-onyx mb-space-2">
          Role codes <span className="text-gold">*</span>
        </div>
        <RoleCheckboxGrid
          roles={roles}
          selected={form.roleCodes}
          onToggle={toggleRole}
        />
        {/* The invite path grants through the same `SetRoleCodesAsync` as the
            edit path, and a BRAND-NEW ACCOUNT is the case that prompted this —
            money reaching someone who has not signed in yet. No existing set is
            passed because there is none: the user does not exist, so every code
            here is an addition by construction. */}
        <RoleGrantCommissionNotice selectedRoleCodes={form.roleCodes} />
      </div>

      {form.roleCodes.length > 1 && (
        <div className="mt-space-4">
          <FormField label="Primary role" htmlFor="invite-primary">
            <Select
              id="invite-primary"
              value={form.primaryRoleCode ?? ""}
              onChange={(e) => setForm({ ...form, primaryRoleCode: e.target.value })}
            >
              {form.roleCodes.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Select>
          </FormField>
        </div>
      )}

      {error && <p className="text-caption text-status-error mt-space-3" role="alert">{error}</p>}
    </Modal>
  );
}

function EditUserModal({ user, onClose, onSaved }: { user: UserSummaryDto; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<UpdateUserPayload>({
    displayName: user.displayName,
    jobTitle: user.jobTitle ?? "",
    department: user.department ?? "",
    isActive: user.isActive,
    clearAvatar: false,
    // ⚠️ HYDRATED, NOT DEFAULTED. This PUT is a full replace, so a form that
    // opened without the current value would CLEAR a closed arrangement on the
    // next unrelated save — reopening it silently, and starting to pay income
    // nobody has looked at since. The `?? ""` is the empty <input type="date">,
    // which the submit turns back into null.
    commissionEndDate: user.commissionEndDate ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateUserPayload) => usersApi.update(user.id, body),
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Edit ${user.displayName}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            onClick={() => {
              setError(null);
              mutation.mutate({
                ...form,
                jobTitle: form.jobTitle || null,
                department: form.department || null,
                commissionEndDate: form.commissionEndDate || null,
              });
            }}
            loading={mutation.isPending}
            disabled={!form.displayName}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        <FormField label="Display name" required htmlFor="edit-name">
          <Input id="edit-name" value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
        </FormField>
        <FormField label="Job title" htmlFor="edit-title">
          <Input id="edit-title" value={form.jobTitle ?? ""}
            onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
        </FormField>
        <FormField label="Department" htmlFor="edit-dept">
          <Input id="edit-dept" value={form.department ?? ""}
            onChange={(e) => setForm({ ...form, department: e.target.value })} />
        </FormField>
      </div>

      {/* `isActive` is nullable on the wire and null means UNCHANGED, so a
          controlled checkbox has to narrow it. This modal is the one surface
          where an admin deliberately sets the flag — it is seeded from the
          record above and always sends an explicit boolean, so the fallback
          here is the record's own current value, which is what "unchanged"
          means. Never let this become an omitted field: omitting it used to
          bind `false` and silently deactivate the account. */}
      <label className="flex items-center gap-space-2 mt-space-3 text-small">
        <input
          type="checkbox"
          checked={form.isActive ?? user.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          className="accent-gold h-4 w-4"
        />
        Account active
      </label>

      {/* U11 — the commission arrangement, beside the account switch because
          the two are read together: a book payee whose account is OFF and whose
          arrangement has no end date gets HELD F1/F2 shares, with a queue item
          asking for exactly this date. Setting it releases them — either to
          her, because the income predates it, or to nobody, because it does
          not.

          A bare <input type="date"> on purpose: it emits `YYYY-MM-DD`, which is
          what a `DateOnly?` binds losslessly. Anything that widened it to an
          instant would store UTC midnight and end the arrangement seven hours
          early, on the day BEFORE the one typed here. */}
      <div className="mt-space-3">
        <FormField
          label="Commission end date"
          htmlFor="edit-commission-end"
          helperText="The last day F1/F2 book commission is paid on this person's income — the termination date plus the notice their agreement gives them. Inclusive. Leave blank while the arrangement is open-ended."
        >
          <Input
            id="edit-commission-end"
            type="date"
            value={form.commissionEndDate ?? ""}
            onChange={(e) => setForm({ ...form, commissionEndDate: e.target.value })}
          />
        </FormField>
      </div>

      {/* The avatar URL is user-owned (profile photo upload) and can't be
          set by an admin — only cleared. */}
      {user.avatarUrl && (
        <label className="flex items-center gap-space-2 mt-space-3 text-small">
          <input
            type="checkbox"
            checked={form.clearAvatar ?? false}
            onChange={(e) => setForm({ ...form, clearAvatar: e.target.checked })}
            className="accent-gold h-4 w-4"
          />
          Clear avatar image
        </label>
      )}

      {error && <p className="text-caption text-status-error mt-space-3" role="alert">{error}</p>}
    </Modal>
  );
}

function AssignRolesModal({
  user,
  roles,
  onClose,
  onSaved,
}: {
  user: UserSummaryDto;
  roles: RoleCodeOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(user.roleCodes);
  // Initialize from the STORED primary (now on the DTO), not roleCodes[0] —
  // saving the modal must not silently move a user's primary role.
  const [primary, setPrimary] = useState<string | undefined>(
    user.primaryRoleCode ?? user.roleCodes[0],
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: AssignRolesPayload) => usersApi.assignRoles(user.id, body),
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  const toggle = (code: string) => {
    setSelected((cur) => {
      const has = cur.includes(code);
      const next = has ? cur.filter((c) => c !== code) : [...cur, code];
      setPrimary((p) => (next.includes(p ?? "") ? p : next[0]));
      return next;
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Role codes · ${user.displayName}`}
      description="A user can hold multiple role codes. The primary code drives their dashboard and default sidebar."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            onClick={() => {
              setError(null);
              mutation.mutate({ roleCodes: selected, primaryRoleCode: primary });
            }}
            loading={mutation.isPending}
            disabled={selected.length === 0}
          >
            Save roles
          </Button>
        </>
      }
    >
      <RoleCheckboxGrid roles={roles} selected={selected} onToggle={toggle} />

      {/* 🔴 THE FULL SELECTED SET AND WHAT THEY HOLD TODAY, not the difference.
          The notice still describes ADDITIONS only — re-saving a code somebody
          already holds writes the same row and routes nothing, and attaching a
          money consequence to an act that has none is how a notice gets learned
          past — but it computes that difference itself, because one of its
          statements is about the RESULTING set rather than the delta: holding
          C1 and F1 together supersedes the C1 share on the loans in that
          person's F1 book, and that is equally true whichever of the two is
          being added. A caller passing only the difference makes the case
          unreachable from one direction. */}
      <RoleGrantCommissionNotice
        selectedRoleCodes={selected}
        existingRoleCodes={user.roleCodes}
      />

      {selected.length > 1 && (
        <div className="mt-space-4">
          <FormField label="Primary role" htmlFor="roles-primary">
            <Select
              id="roles-primary"
              value={primary ?? ""}
              onChange={(e) => setPrimary(e.target.value)}
            >
              {selected.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </FormField>
        </div>
      )}
      {error && <p className="text-caption text-status-error mt-space-3" role="alert">{error}</p>}
    </Modal>
  );
}

function DeactivateModal({ user, onClose, onConfirmed }: { user: UserSummaryDto; onClose: () => void; onConfirmed: () => void }) {
  const mutation = useMutation({
    mutationFn: () => usersApi.deactivate(user.id),
    onSuccess: onConfirmed,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Deactivate ${user.displayName}?`}
      description="The user loses access immediately. History and audit entries are preserved. You can reactivate them later via the edit form."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Keep active</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} loading={mutation.isPending}>
            Deactivate
          </Button>
        </>
      }
    >
      <div className="text-small text-gray-mid">
        Email: <span className="font-mono">{user.email}</span>
        <br />
        Roles: <span className="font-mono">{user.roleCodes.join(", ") || "none"}</span>
      </div>
    </Modal>
  );
}

function RoleCheckboxGrid({
  roles,
  selected,
  onToggle,
}: {
  roles: RoleCodeOption[];
  selected: string[];
  onToggle: (code: string) => void;
}) {
  if (roles.length === 0) {
    return <p className="text-caption text-gray-mid">Loading role catalogue…</p>;
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-space-2">
      {roles.map((r) => {
        const checked = selected.includes(r.code);
        return (
          <label
            key={r.code}
            className={`flex items-start gap-space-2 p-space-2 rounded-md border cursor-pointer ${
              checked ? "border-gold bg-gold/5" : "border-border-light hover:bg-white-warm"
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(r.code)}
              className="mt-space-1 h-4 w-4 accent-gold"
            />
            <div>
              <div className="font-mono text-small font-semibold text-onyx">{r.code}</div>
              <div className="text-caption text-gray-mid leading-tight">{r.title}</div>
            </div>
          </label>
        );
      })}
    </div>
  );
}
